import { useEffect } from "react";
import { useLocation } from "react-router-dom";

const SITE_NAME = "Flames Nova";
const SITE_URL = "https://flamesnova.name.ng";
const CEO_NAME = "Agbasionwe Emmanuel Chiemelie";
const LOGO = `${SITE_URL}/icon-512.png`;

// Telecom data offers (aligned with on-page pricing)
const dataPlans = [
  { sku: "FN-MTN-1GB-SME", name: "MTN 1GB SME Data Bundle", price: 265, was: 300, network: "MTN", dataSize: "1GB", validity: "30 Days" },
  { sku: "FN-MTN-2GB-SME", name: "MTN 2GB SME Data Bundle", price: 530, was: 600, network: "MTN", dataSize: "2GB", validity: "30 Days" },
  { sku: "FN-MTN-5GB-SME", name: "MTN 5GB SME Data Bundle", price: 1325, was: 1500, network: "MTN", dataSize: "5GB", validity: "30 Days" },
  { sku: "FN-AIRTEL-1GB", name: "Airtel 1GB Corporate Data Bundle", price: 265, was: 300, network: "Airtel", dataSize: "1GB", validity: "30 Days" },
  { sku: "FN-GLO-1GB", name: "Glo 1GB Data Gift Bundle", price: 265, was: 300, network: "Glo", dataSize: "1GB", validity: "30 Days" },
  { sku: "FN-9MOBILE-1GB", name: "9mobile 1GB Corporate Data Bundle", price: 265, was: 300, network: "9mobile", dataSize: "1GB", validity: "30 Days" },
];

const baseSchemas = (): object[] => [
  {
    "@context": "https://schema.org",
    "@type": ["Organization", "FinancialService"],
    "@id": `${SITE_URL}/#organization`,
    name: SITE_NAME,
    legalName: "Flames Nova Technology Ecosystem",
    url: SITE_URL,
    logo: {
      "@type": "ImageObject",
      url: LOGO,
      width: 512,
      height: 512,
    },
    image: LOGO,
    description: "Nigeria's automated telecom vending infrastructure, utility bill payment platform, and free AI reasoning assistant.",
    founder: {
      "@type": "Person",
      "@id": `${SITE_URL}/#founder`,
      name: CEO_NAME,
      jobTitle: "Founder & Chief Executive Officer",
      nationality: { "@type": "Country", name: "Nigeria" },
      sameAs: [`${SITE_URL}/about`],
    },
    address: {
      "@type": "PostalAddress",
      addressCountry: "NG",
      addressRegion: "Anambra State",
      addressLocality: "Nando",
    },
    areaServed: { "@type": "Country", name: "Nigeria" },
    currenciesAccepted: "NGN",
    paymentAccepted: ["Bank Transfer", "Debit Card", "Credit Card", "Paystack", "USSD"],
    priceRange: "₦",
    contactPoint: {
      "@type": "ContactPoint",
      contactType: "Customer Support",
      areaServed: "NG",
      availableLanguage: ["English"],
      email: "anointed1234567890@gmail.com",
      url: `${SITE_URL}/contact`,
    },
    sameAs: [
      SITE_URL,
      "https://vtu.flamesnova.name.ng",
      "https://ai.flamesnova.name.ng",
      "https://store.flamesnova.name.ng",
      "https://whatsapp.com/channel/flamesnova",
    ],
  },
  {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": `${SITE_URL}/#website`,
    name: SITE_NAME,
    url: SITE_URL,
    publisher: { "@id": `${SITE_URL}/#organization` },
    potentialAction: {
      "@type": "SearchAction",
      target: {
        "@type": "EntryPoint",
        urlTemplate: `${SITE_URL}/services/data?q={search_term_string}`,
      },
      "query-input": "required name=search_term_string",
    },
    inLanguage: "en-NG",
  },
];

const prettifySegment = (seg: string) =>
  seg.replace(/-/g, " ").replace(/\w/g, (c) => c.toUpperCase());

const breadcrumbSchema = (path: string) => {
  const segments = path.split("/").filter(Boolean);
  const items: object[] = [
    { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
  ];
  let acc = "";
  segments.forEach((seg, i) => {
    acc += `/${seg}`;
    items.push({
      "@type": "ListItem",
      position: i + 2,
      name: prettifySegment(decodeURIComponent(seg)),
      item: `${SITE_URL}${acc}`,
    });
  });
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items,
  };
};

const productSchemas = (): object[] =>
  dataPlans.map((p) => ({
    "@context": "https://schema.org",
    "@type": "Product",
    name: p.name,
    sku: p.sku,
    mpn: p.sku,
    description: `${p.name} (${p.dataSize}, ${p.validity}) — instant automated delivery on ${SITE_NAME} across Nigeria.`,
    image: [LOGO],
    brand: { "@type": "Brand", name: p.network },
    category: "Mobile Data",
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: "4.9",
      reviewCount: "1350",
      bestRating: "5",
      worstRating: "1",
    },
    offers: {
      "@type": "Offer",
      price: p.price,
      priceCurrency: "NGN",
      availability: "https://schema.org/InStock",
      itemCondition: "https://schema.org/NewCondition",
      url: `${SITE_URL}/services/data`,
      priceValidUntil: "2027-12-31",
      validFrom: "2024-01-01",
      seller: { "@type": "Organization", name: SITE_NAME },
      priceSpecification: {
        "@type": "UnitPriceSpecification",
        price: p.was,
        priceCurrency: "NGN",
        priceType: "https://schema.org/ListPrice",
      },
      shippingDetails: {
        "@type": "OfferShippingDetails",
        shippingRate: {
          "@type": "MonetaryAmount",
          value: "0",
          currency: "NGN",
        },
        shippingDestination: {
          "@type": "DefinedRegion",
          addressCountry: "NG",
        },
        deliveryTime: {
          "@type": "ShippingDeliveryTime",
          handlingTime: {
            "@type": "QuantitativeValue",
            minValue: 0,
            maxValue: 0,
            unitCode: "DAY",
          },
          transitTime: {
            "@type": "QuantitativeValue",
            minValue: 0,
            maxValue: 0,
            unitCode: "DAY",
          },
        },
      },
      hasMerchantReturnPolicy: {
        "@type": "MerchantReturnPolicy",
        applicableCountry: "NG",
        returnPolicyCategory: "https://schema.org/MerchantReturnNotPermitted",
        merchantReturnDays: 0,
      },
    },
  }));

const faqSchema = () => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    {
      "@type": "Question",
      name: "How fast is data and airtime delivered on Flames Nova?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Delivery is fully automated and occurs within 1 to 5 seconds immediately after payment confirmation.",
      },
    },
    {
      "@type": "Question",
      name: "How do I convert airtime to cash?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Navigate to Airtime to Cash, select your network (MTN, Airtel, Glo, 9mobile), input the airtime amount, transfer to the designated receiver number, and receive instant cash in your bank account or wallet.",
      },
    },
    {
      "@type": "Question",
      name: "What networks are supported for cheap data?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Flames Nova supports MTN (SME, Corporate Gifting, Direct Gifting), Airtel (Corporate, Gifting), Glo (Gift, SME), and 9mobile.",
      },
    },
    {
      "@type": "Question",
      name: "Which electricity distribution companies (DISCOs) can I pay for?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "We support all Nigerian electricity companies including IKEDC, EKEDC, AEDC, IBEDC, PHED, EEDC, KEDCO, and JED for both prepaid meter tokens and postpaid bills.",
      },
    },
    {
      "@type": "Question",
      name: "Is Flames Nova AI free to use?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Yes, Flames Nova AI provides free reasoning, coding assistance, and 17 SEO tools with no mandatory subscription.",
      },
    },
  ],
});

const JsonLdSchema = () => {
  const location = useLocation();

  useEffect(() => {
    const path = location.pathname;
    const isBlogPage = path === "/blog" || path.startsWith("/blog/");

    // Remove ratings inherited from the shared SPA shell. There is no persisted
    // article-review source, so article pages must not claim app/product ratings.
    if (isBlogPage) {
      document.querySelectorAll<HTMLScriptElement>('script[type="application/ld+json"]').forEach((script) => {
        try {
          const schema = JSON.parse(script.textContent || "{}");
          const types = Array.isArray(schema?.["@type"]) ? schema["@type"] : [schema?.["@type"]];
          if (types.includes("Review") || (schema?.aggregateRating && types.some((type: string) => type === "SoftwareApplication" || type === "Product"))) {
            script.remove();
          }
        } catch {
          // Do not remove unrelated structured data that cannot be parsed.
        }
      });
    }
    const isBlogPost = path.startsWith("/blog/") || path.startsWith("/articles/") || path.startsWith("/tools/");

    // Individual blog posts own their Article + BreadcrumbList markup (BlogPostPage + worker SSR).
    const schemas: object[] = isBlogPost
      ? [...baseSchemas()]
      : [...baseSchemas(), breadcrumbSchema(path)];

    const isCommercial =
      path === "/" || path === "/home" || path.startsWith("/services") || path.startsWith("/wallet");

    if (isCommercial) {
      schemas.push(...productSchemas(), faqSchema());
    }

    // Specific Service Schemas
    if (path === "/services/data" || path === "/data-plans" || path === "/data-prices") {
      schemas.push({
        "@context": "https://schema.org",
        "@type": "Service",
        name: "Cheap SME Mobile Data Bundles Nigeria",
        serviceType: "Mobile Data Telecom Vending",
        provider: { "@type": "Organization", name: SITE_NAME, url: SITE_URL },
        areaServed: { "@type": "Country", name: "Nigeria" },
        description: "Instant SME, Corporate, and Direct mobile data top-up for MTN, Airtel, Glo, and 9mobile in Nigeria.",
        offers: {
          "@type": "AggregateOffer",
          lowPrice: "140",
          highPrice: "50000",
          priceCurrency: "NGN",
          offerCount: "120",
        },
      });
    }

    if (path === "/services/airtime") {
      schemas.push({
        "@context": "https://schema.org",
        "@type": "Service",
        name: "Discounted Airtime Recharge Nigeria",
        serviceType: "VTU Airtime Top-Up",
        provider: { "@type": "Organization", name: SITE_NAME, url: SITE_URL },
        areaServed: { "@type": "Country", name: "Nigeria" },
        description: "Instant discounted airtime top-up for MTN, Airtel, Glo, and 9mobile with up to 3% discount and sub-second automated delivery.",
      });
    }

    if (path === "/airtime-to-cash" || path === "/services/airtime-to-cash" || path === "/airtime-to-cash-calculator" || path === "/compare-airtime-to-cash") {
      schemas.push({
        "@context": "https://schema.org",
        "@type": "FinancialService",
        name: "Airtime to Cash Instant Conversion Nigeria",
        serviceType: "Airtime Liquidation & Bank Payout",
        provider: { "@type": "Organization", name: SITE_NAME, url: SITE_URL },
        areaServed: { "@type": "Country", name: "Nigeria" },
        description: "Convert excess MTN, Airtel, Glo, and 9mobile airtime into instant cash deposited straight into your Nigerian bank account or Flames Nova wallet.",
        currenciesAccepted: "NGN",
        paymentAccepted: "Airtime Transfer, Bank Transfer",
        priceRange: "₦",
      });
    }

    if (path === "/services/electricity") {
      schemas.push({
        "@context": "https://schema.org",
        "@type": "Service",
        name: "Prepaid Electricity Meter Token Recharge (All DISCOs)",
        serviceType: "Utility Bill Payment",
        provider: { "@type": "Organization", name: SITE_NAME, url: SITE_URL },
        areaServed: { "@type": "Country", name: "Nigeria" },
        description: "Instant prepaid meter token generation and postpaid bill payments for IKEDC, EKEDC, AEDC, IBEDC, PHED, EEDC, KEDCO, and JED.",
      });
    }

    if (path === "/services/cable") {
      schemas.push({
        "@context": "https://schema.org",
        "@type": "Service",
        name: "Cable TV Decoder Subscription Renewal (DStv, GOtv, StarTimes)",
        serviceType: "Pay TV Subscription",
        provider: { "@type": "Organization", name: SITE_NAME, url: SITE_URL },
        areaServed: { "@type": "Country", name: "Nigeria" },
        description: "Instant renewal and package upgrade for DStv, GOtv, and StarTimes with automated smartcard verification.",
      });
    }

    if (path === "/services/exam-pins" || path === "/education") {
      schemas.push({
        "@context": "https://schema.org",
        "@type": "Product",
        name: "WAEC, NECO & JAMB Result Checker PINs",
        description: "Instant examination scratch cards and result checker PIN tokens for WAEC, NECO, and JAMB.",
        category: "Educational Vouchers",
        image: [LOGO],
        offers: {
          "@type": "AggregateOffer",
          lowPrice: "1200",
          highPrice: "4500",
          priceCurrency: "NGN",
          availability: "https://schema.org/InStock",
        },
      });
    }

    if (path === "/services/recharge-card") {
      schemas.push({
        "@context": "https://schema.org",
        "@type": "Service",
        name: "Universal Recharge Card & e-PIN Printing",
        serviceType: "e-PIN Generation",
        provider: { "@type": "Organization", name: SITE_NAME, url: SITE_URL },
        areaServed: { "@type": "Country", name: "Nigeria" },
        description: "Generate and print branded airtime and data e-PIN recharge cards for all Nigerian telecom networks.",
      });
    }

    if (path === "/services/virtual-number" || path === "/services/sms-verify") {
      schemas.push({
        "@context": "https://schema.org",
        "@type": "Service",
        name: "Virtual Temporary Phone Numbers & SMS OTP Verification",
        serviceType: "Virtual Telecom Numbers",
        provider: { "@type": "Organization", name: SITE_NAME, url: SITE_URL },
        description: "Temporary and non-VoIP virtual phone numbers for online SMS and OTP verification paid in Nigerian Naira.",
      });
    }

    // AI & Developer surfaces
    if (path === "/ai" || path.startsWith("/ai/") || path === "/chat") {
      schemas.push({
        "@context": "https://schema.org",
        "@type": "SoftwareApplication",
        name: "Flames Nova AI Assistant",
        applicationCategory: "AIApplication",
        operatingSystem: "Web, Cloud",
        url: `${SITE_URL}/ai`,
        description: "Autonomous reasoning and coding intelligence powered by Apex R1, Prism Coder Pro, live real-time web research, and 17 SEO tools.",
        offers: { "@type": "Offer", price: "0", priceCurrency: "NGN" },
      });
    }

    if (path === "/mcp" || path.startsWith("/mcp/")) {
      schemas.push({
        "@context": "https://schema.org",
        "@type": "SoftwareApplication",
        name: "Flames Nova Model Context Protocol (MCP) Server",
        applicationCategory: "DeveloperApplication",
        operatingSystem: "Cross-Platform",
        url: `${SITE_URL}/mcp`,
        description: "Official Anthropic Model Context Protocol (MCP) server providing real-time VTU telecom pricing, wallet transactions, and blog knowledge to AI agents like Claude and ChatGPT.",
        offers: { "@type": "Offer", price: "0", priceCurrency: "NGN" },
      });
    }

    if (path === "/about") {
      schemas.push({
        "@context": "https://schema.org",
        "@type": "AboutPage",
        name: `About ${SITE_NAME}`,
        url: `${SITE_URL}/about`,
        mainEntity: {
          "@type": "Person",
          name: CEO_NAME,
          jobTitle: "Founder & Chief Executive Officer",
          worksFor: { "@type": "Organization", name: SITE_NAME, url: SITE_URL },
          nationality: { "@type": "Country", name: "Nigeria" },
        },
      });
    }

    if (path === "/contact") {
      schemas.push({
        "@context": "https://schema.org",
        "@type": "ContactPage",
        name: `Contact ${SITE_NAME} Support`,
        url: `${SITE_URL}/contact`,
        mainEntity: {
          "@type": "ContactPoint",
          contactType: "Customer Support",
          email: "anointed1234567890@gmail.com",
          availableLanguage: ["English"],
          areaServed: "NG",
        },
      });
    }

    if (path === "/faq") {
      schemas.push(faqSchema());
    }

    if (path === "/blog") {
      schemas.push(
        {
          "@context": "https://schema.org",
          "@type": "Blog",
          "@id": `${SITE_URL}/blog#blog`,
          name: "Flames Nova Blog",
          url: `${SITE_URL}/blog`,
          description: "Read in-depth guides, telecom updates, cheap data tips, fintech architecture, and AI tutorials from Flames Nova.",
          inLanguage: "en-NG",
          publisher: {
            "@type": "Organization",
            "@id": `${SITE_URL}/#organization`,
            name: "Flames Nova",
            url: SITE_URL,
            logo: {
              "@type": "ImageObject",
              url: `${SITE_URL}/icon-512.png`,
              width: 512,
              height: 512,
            },
          },
        },
        {
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          "@id": `${SITE_URL}/blog#collection`,
          name: "Flames Nova Blog — Tech, Telecom & AI Guides",
          url: `${SITE_URL}/blog`,
          description: "Read in-depth guides, telecom updates, cheap data tips, fintech architecture, and AI tutorials from Flames Nova.",
          isPartOf: {
            "@type": "WebSite",
            "@id": `${SITE_URL}/#website`,
            name: "Flames Nova",
            url: SITE_URL,
          },
        },
        {
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
            { "@type": "ListItem", position: 2, name: "Blog", item: `${SITE_URL}/blog` },
          ],
        }
      );
    }

    document.querySelectorAll('script[data-jsonld="flames-nova"]').forEach((s) => s.remove());
    schemas.forEach((schema) => {
      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.setAttribute("data-jsonld", "flames-nova");
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
    });

    return () => {
      document.querySelectorAll('script[data-jsonld="flames-nova"]').forEach((s) => s.remove());
    };
  }, [location.pathname]);

  return null;
};

export default JsonLdSchema;
